/**
 * A file has exactly one name — `filename`, with its extension — and the tree
 * renders it without one. That strip is presentation, computed where a file row
 * becomes a tree row, and never stored.
 *
 * This is the last extension-aware code in the client. It strips for display
 * and never sends: `PUT /api/files/{id}` composes the stored name from the
 * row's own extension, so nothing here can change what a file is.
 */

/** The filename minus its last dot-token. `Q3 Board Pack.xlsx` → `Q3 Board Pack`. */
export function fileStem(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot < 0 ? filename : filename.slice(0, dot);
}

/** The last dot-token, with its dot. `Q3 Board Pack.xlsx` → `.xlsx`, `Report` → ``. */
export function fileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot < 0 ? '' : filename.slice(dot);
}

/**
 * Compose the name a rename would store: the stem the user typed plus the
 * extension the row already has. The server does exactly this, and the client
 * needs it too — to tell, *before* sending, whether the name it is about to ask
 * for is one a sibling already holds.
 */
export function composeFilename(stem: string, current: string | undefined): string {
  return stem + fileExtension(current ?? '');
}

/**
 * True when a sibling shares this item's rendered stem.
 *
 * The tree hides extensions, which makes `report.pdf` and `report.xlsx` — two
 * genuinely different files, on the mount and in the database — look like one
 * name shown twice. This is the one case where hiding the extension hides a
 * real distinction, so the row renders it back.
 *
 * Compared case-sensitively, like everything else about a name: `Q1` and `q1`
 * are two stems, not one shown twice.
 */
/**
 * `Q1.xlsx` → `Q1 (2).xlsx` — the client half of "Keep both".
 *
 * The server's own allocator (`storage_paths.suffixed_filename`) has the same
 * shape, and the two must not disagree about what the second copy is called:
 * the user picked "Keep both" expecting the name shown in the dialog.
 */
export function nextFreeFilename(filename: string, taken: Set<string>): string {
  if (!taken.has(filename)) return filename;
  const stem = fileStem(filename);
  const ext = fileExtension(filename);
  for (let n = 2; ; n++) {
    const candidate = `${stem} (${n})${ext}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** The extension a `file_type` implies; any other is worth showing. */
const CANONICAL_EXTENSION: Record<string, string> = {
  excel: '.xlsx',
  pdf: '.pdf',
  powerpoint: '.pptx',
  word: '.docx',
};

/**
 * The tree hides extensions. Two cases hide a real distinction, and the row
 * renders the extension back: a sibling stem collision (`stemIsAmbiguous`),
 * and a file whose extension is not the one its type implies — a `.csv` or
 * `.xlsm` under `excel` is a different kind of workbook (one sheet, no formulas).
 */
export function extensionIsNotable(filename: string | undefined, fileType?: string): boolean {
  if (!filename) return false;
  const ext = fileExtension(filename).toLowerCase();
  const canonical = fileType ? CANONICAL_EXTENSION[fileType] : undefined;
  return !!ext && !!canonical && ext !== canonical;
}

export function stemIsAmbiguous(
  item: { id: string; name: string },
  siblings: { id: string; name: string }[],
): boolean {
  return siblings.some((s) => s.id !== item.id && s.name === item.name);
}
